import { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const fmtCurrency = (n) => n ? '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '—';

const emptyPacket = {
  agreed_standards: false, agreed_materials: false, agreed_license: false, agreed_cleanliness: false,
  stories: '', access_level: 'Easy', tearoff_layers: '', start_requested: '', bid_due: '',
  labor_cost: '', materials_cost: '', dump_fees: '', permit_fees: '', total_bid: '',
  start_date: '', duration_days: '', crew_size: '', warranty: '',
  squares: '', shingle_brand: 'Owens Corning', shingle_color: '', underlayment: 'Synthetic',
  starter_bundles: '', hip_ridge_bundles: '', ridge_vent_lf: '', vents_qty: '',
  drip_edge_lf: '', flashing: '', osb_sheets: '', fascia: '', dumpsters: '',
  paint_gallons: '', primer: '', repairs_needed: '', windows_qty: '', windows_type: '', sizes_attached: false,
  delivery_date: '', start_confirmed: '', inspection_required: false, notes: '',
};

export default function ContractorPacket({ job, onClose }) {
  const [packets, setPackets] = useState([]);
  const [form, setForm] = useState(emptyPacket);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewingPacket, setViewingPacket] = useState(null);

  useEffect(() => { loadPackets(); }, [job.id]);

  async function loadPackets() {
    try {
      const res = await api.get(`/contractor-packets/job/${job.id}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setPackets(data);
      if (data.length === 0) setShowForm(true);
    } catch {}
  }

  function calcTotal() {
    const total = (parseFloat(form.labor_cost) || 0) + (parseFloat(form.materials_cost) || 0) +
      (parseFloat(form.dump_fees) || 0) + (parseFloat(form.permit_fees) || 0);
    setForm(f => ({ ...f, total_bid: total || '' }));
  }

  async function handleSubmit() {
    if (!form.agreed_standards || !form.agreed_materials || !form.agreed_license || !form.agreed_cleanliness) {
      return toast.error('Please check all agreement items');
    }
    if (!form.labor_cost && !form.total_bid) return toast.error('Please enter at least labor cost or total bid');
    setSubmitting(true);
    try {
      const res = await api.post('/contractor-packets', { ...form, job_id: job.id, po_number: job.po_number });
      toast.success(`Packet submitted (Submission #${res.data.submission_number}). ${res.data.remaining} submission(s) remaining.`);
      setShowForm(false);
      setForm(emptyPacket);
      loadPackets();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally { setSubmitting(false); }
  }

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const canSubmit = packets.length < 2;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, overflowY: 'auto', padding: '20px 16px' }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 760, margin: '0 auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ background: '#0D3B2E', color: '#fff', padding: '18px 24px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'DM Serif Display, serif', fontSize: 20 }}>CPR HOME SOLUTIONS</div>
            <div style={{ fontSize: 13, color: '#ccc', marginTop: 2 }}>Master Contractor Packet — {job.po_number}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 22 }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {/* Job info banner */}
          <div style={{ background: '#e8f5e9', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, fontSize: 13 }}>
            <div><strong>Homeowner:</strong> {job.customer_name}</div>
            <div><strong>Address:</strong> {job.address} {job.city}</div>
            <div><strong>Phone:</strong> {job.phone || '—'}</div>
            <div><strong>Scope:</strong> {job.scope || '—'}</div>
            <div><strong>Color:</strong> {job.color || '—'}</div>
            <div><strong>Permit:</strong> {job.permit || '—'}</div>
          </div>

          {/* Previous submissions */}
          {packets.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0D3B2E', marginBottom: 10 }}>
                Previous Submissions ({packets.length}/2)
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {packets.map(p => (
                  <button key={p.id} onClick={() => setViewingPacket(viewingPacket?.id === p.id ? null : p)}
                    style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #0D3B2E', background: viewingPacket?.id === p.id ? '#0D3B2E' : '#fff', color: viewingPacket?.id === p.id ? '#fff' : '#0D3B2E', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    Submission #{p.submission_number} — {p.created_at?.slice(0,10)}
                  </button>
                ))}
              </div>

              {/* View previous packet */}
              {viewingPacket && (
                <div style={{ marginTop: 12, background: '#f9f9f9', borderRadius: 8, padding: '16px', fontSize: 13 }}>
                  <div style={{ fontWeight: 700, marginBottom: 10, color: '#0D3B2E' }}>Submission #{viewingPacket.submission_number}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    <div>
                      <SectionTitle>Bid</SectionTitle>
                      <InfoRow label="Labor" value={fmtCurrency(viewingPacket.labor_cost)} />
                      <InfoRow label="Materials" value={fmtCurrency(viewingPacket.materials_cost)} />
                      <InfoRow label="Dump Fees" value={fmtCurrency(viewingPacket.dump_fees)} />
                      <InfoRow label="Permit Fees" value={fmtCurrency(viewingPacket.permit_fees)} />
                      <InfoRow label="TOTAL" value={fmtCurrency(viewingPacket.total_bid)} />
                      <InfoRow label="Start Date" value={viewingPacket.start_date || '—'} />
                      <InfoRow label="Duration" value={viewingPacket.duration_days ? viewingPacket.duration_days + ' days' : '—'} />
                      <InfoRow label="Crew Size" value={viewingPacket.crew_size || '—'} />
                    </div>
                    <div>
                      <SectionTitle>Materials</SectionTitle>
                      <InfoRow label="Squares" value={viewingPacket.squares || '—'} />
                      <InfoRow label="Shingle" value={viewingPacket.shingle_brand || '—'} />
                      <InfoRow label="Color" value={viewingPacket.shingle_color || '—'} />
                      <InfoRow label="Underlayment" value={viewingPacket.underlayment || '—'} />
                      <InfoRow label="OSB Sheets" value={viewingPacket.osb_sheets || '—'} />
                      <InfoRow label="Dumpsters" value={viewingPacket.dumpsters || '—'} />
                    </div>
                    <div>
                      <SectionTitle>Scheduling</SectionTitle>
                      <InfoRow label="Delivery Date" value={viewingPacket.delivery_date || '—'} />
                      <InfoRow label="Start Confirmed" value={viewingPacket.start_confirmed || '—'} />
                      <InfoRow label="Inspection" value={viewingPacket.inspection_required ? 'Yes' : 'No'} />
                      <InfoRow label="Stories" value={viewingPacket.stories || '—'} />
                      <InfoRow label="Access" value={viewingPacket.access_level || '—'} />
                      <InfoRow label="Tear-off Layers" value={viewingPacket.tearoff_layers || '—'} />
                      {viewingPacket.notes && <div style={{ marginTop: 8, color: '#555' }}><strong>Notes:</strong> {viewingPacket.notes}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* New submission form */}
          {canSubmit && (
            <>
              {!showForm ? (
                <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                  + Submit {packets.length === 1 ? 'Revised' : 'New'} Packet
                </button>
              ) : (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0D3B2E', marginBottom: 16, paddingBottom: 8, borderBottom: '2px solid #e8f5e9' }}>
                    {packets.length === 1 ? 'Revision — Submission #2' : 'New Submission'}
                  </div>

                  {/* Section 1 — Agreement */}
                  <Section title="1. Contractor Agreement & Expectations">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        ['agreed_standards', 'I agree to follow CPR Home Solutions installation standards'],
                        ['agreed_materials', 'I agree all materials must be listed prior to project start'],
                        ['agreed_license', 'I agree to maintain active license and insurance'],
                        ['agreed_cleanliness', 'I agree to maintain jobsite cleanliness and professionalism'],
                      ].map(([field, label]) => (
                        <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={form[field]} onChange={e => set(field, e.target.checked)} style={{ width: 16, height: 16, accentColor: '#1B5E20' }} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </Section>

                  {/* Section 2 — Bid Request */}
                  <Section title="2. Bid Request Summary">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                      <FormField label="Stories" value={form.stories} onChange={v => set('stories', v)} placeholder="e.g. 1" />
                      <FormField label="Access" type="select" value={form.access_level} onChange={v => set('access_level', v)} options={['Easy','Moderate','Difficult']} />
                      <FormField label="Tear-Off Layers" value={form.tearoff_layers} onChange={v => set('tearoff_layers', v)} />
                      <FormField label="Start Requested" type="date" value={form.start_requested} onChange={v => set('start_requested', v)} />
                      <FormField label="Bid Due" type="date" value={form.bid_due} onChange={v => set('bid_due', v)} />
                    </div>
                  </Section>

                  {/* Section 3 — Bid Submission */}
                  <Section title="3. Bid Submission">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                      <FormField label="Labor ($)" type="number" value={form.labor_cost} onChange={v => { set('labor_cost', v); }} onBlur={calcTotal} />
                      <FormField label="Materials ($)" type="number" value={form.materials_cost} onChange={v => set('materials_cost', v)} onBlur={calcTotal} />
                      <FormField label="Dump Fees ($)" type="number" value={form.dump_fees} onChange={v => set('dump_fees', v)} onBlur={calcTotal} />
                      <FormField label="Permit Fees ($)" type="number" value={form.permit_fees} onChange={v => set('permit_fees', v)} onBlur={calcTotal} />
                      <div>
                        <label style={lblStyle}>TOTAL BID ($)</label>
                        <input className="form-input" type="number" value={form.total_bid} onChange={e => set('total_bid', e.target.value)}
                          style={{ fontWeight: 700, fontSize: 15, color: '#0D3B2E' }} />
                      </div>
                      <FormField label="Start Date" type="date" value={form.start_date} onChange={v => set('start_date', v)} />
                      <FormField label="Duration (Days)" value={form.duration_days} onChange={v => set('duration_days', v)} />
                      <FormField label="Crew Size" value={form.crew_size} onChange={v => set('crew_size', v)} />
                      <div style={{ gridColumn: '1/-1' }}>
                        <FormField label="Warranty" value={form.warranty} onChange={v => set('warranty', v)} placeholder="e.g. 1 year labor warranty" />
                      </div>
                    </div>
                  </Section>

                  {/* Section 4 — Material Order */}
                  <Section title="4. Material Order – Roofing System">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                      <FormField label="Squares" value={form.squares} onChange={v => set('squares', v)} />
                      <FormField label="Shingle Brand" value={form.shingle_brand} onChange={v => set('shingle_brand', v)} />
                      <FormField label="Color" value={form.shingle_color} onChange={v => set('shingle_color', v)} />
                      <FormField label="Underlayment" type="select" value={form.underlayment} onChange={v => set('underlayment', v)} options={['Synthetic','Peel & Stick']} />
                      <FormField label="Starter Bundles" value={form.starter_bundles} onChange={v => set('starter_bundles', v)} />
                      <FormField label="Hip/Ridge Bundles" value={form.hip_ridge_bundles} onChange={v => set('hip_ridge_bundles', v)} />
                      <FormField label="Ridge Vent (LF)" value={form.ridge_vent_lf} onChange={v => set('ridge_vent_lf', v)} />
                      <FormField label="Vents (qty)" value={form.vents_qty} onChange={v => set('vents_qty', v)} />
                      <FormField label="Drip Edge (LF)" value={form.drip_edge_lf} onChange={v => set('drip_edge_lf', v)} />
                      <FormField label="Flashing" value={form.flashing} onChange={v => set('flashing', v)} />
                      <FormField label="OSB Sheets" value={form.osb_sheets} onChange={v => set('osb_sheets', v)} />
                      <FormField label="Fascia" value={form.fascia} onChange={v => set('fascia', v)} />
                      <FormField label="Dumpsters" value={form.dumpsters} onChange={v => set('dumpsters', v)} />
                      <FormField label="Tear-Off Layers" value={form.tearoff_layers} onChange={v => set('tearoff_layers', v)} />
                    </div>
                  </Section>

                  {/* Section 5 — Additional Materials */}
                  <Section title="5. Additional Materials (If Applicable)">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                      <FormField label="Paint Gallons" value={form.paint_gallons} onChange={v => set('paint_gallons', v)} />
                      <FormField label="Primer" value={form.primer} onChange={v => set('primer', v)} />
                      <FormField label="Windows Qty" value={form.windows_qty} onChange={v => set('windows_qty', v)} />
                      <FormField label="Window Type" value={form.windows_type} onChange={v => set('windows_type', v)} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 24 }}>
                        <input type="checkbox" checked={form.sizes_attached} onChange={e => set('sizes_attached', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#1B5E20' }} />
                        <label style={{ fontSize: 13 }}>Sizes Attached</label>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label style={lblStyle}>Repairs Needed</label>
                      <textarea className="form-textarea" rows={2} value={form.repairs_needed} onChange={e => set('repairs_needed', e.target.value)} placeholder="Describe any additional repairs..." />
                    </div>
                  </Section>

                  {/* Section 6 — Scheduling */}
                  <Section title="6. Scheduling & Delivery">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                      <FormField label="Material Delivery Date" type="date" value={form.delivery_date} onChange={v => set('delivery_date', v)} />
                      <FormField label="Start Date Confirmed" type="date" value={form.start_confirmed} onChange={v => set('start_confirmed', v)} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 24 }}>
                        <input type="checkbox" checked={form.inspection_required} onChange={e => set('inspection_required', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#1B5E20' }} />
                        <label style={{ fontSize: 13 }}>Inspection Required</label>
                      </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <label style={lblStyle}>Notes</label>
                      <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes..." />
                    </div>
                  </Section>

                  {/* Section 7 — Final Confirmation */}
                  <Section title="7. Final Contractor Confirmation">
                    <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#555', marginBottom: 16 }}>
                      By submitting this packet, I confirm all information provided is accurate and complete. I understand missing items may delay the project and are my responsibility.
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Submitting…' : `Submit ${packets.length === 1 ? 'Revised ' : ''}Packet`}
                      </button>
                    </div>
                  </Section>
                </div>
              )}
            </>
          )}

          {!canSubmit && (
            <div style={{ background: '#fff3e0', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#E65100', marginTop: 16 }}>
              Maximum 2 submissions reached for this job. Contact admin for any further changes.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#0D3B2E', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{children}</div>;
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
      <span style={{ color: '#888' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function FormField({ label, type = 'text', value, onChange, onBlur, options, placeholder }) {
  return (
    <div>
      <label style={lblStyle}>{label}</label>
      {type === 'select' ? (
        <select className="form-select" value={value} onChange={e => onChange(e.target.value)}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input className="form-input" type={type} value={value} onChange={e => onChange(e.target.value)}
          onBlur={onBlur} placeholder={placeholder || ''} />
      )}
    </div>
  );
}

const lblStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
